import { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  FieldInputProps,
  Formik,
  FormikContextType,
  useFormikContext,
} from 'formik';
import * as Yup from 'yup';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { useStore } from '../../../stores/store';
import { CheckboxValues } from '../../../stores/parcelCreationFlowStore';
import { prepareElementBlock } from '../../../utils/elements-parser/elements-parser.utils';
import {
  getNestedElements,
  isDefined,
} from '../../../utils/generic/generic.utils';
import { testIds } from '../../../../test-ids';
import { UIElementInfo } from '../../../models/parcelCreationFlowNavigation';
import { SummaryCheckboxesSection } from './summary-checkbox-section.styles';

interface CheckboxesChangeHandlerProps {
  setSummaryFormIsValid: (value: boolean) => void;
  shouldSubmit: { value: boolean };
}

export const CheckboxesChangeHandler = ({
  setSummaryFormIsValid,
  shouldSubmit,
}: CheckboxesChangeHandlerProps) => {
  const { isValid, submitForm } = useFormikContext();

  useEffect(() => {
    setSummaryFormIsValid(isValid);
  }, [isValid, setSummaryFormIsValid]);

  useEffect(() => {
    if (shouldSubmit.value) {
      submitForm();
    }
  }, [shouldSubmit, submitForm]);

  return null;
};

const isCheckboxElement = (element: UIElementInfo) =>
  element.type === 'input-checkbox' ||
  element.type === 'input-checkbox-select-all';

const SummaryCheckboxSection = () => {
  const {
    parcelCreationStore: {
      setSummaryConfirmFormValues,
      summaryConfirmFormValues,
      setSummaryFormIsValid,
      navigation: { current: currentStep },
    },
    parcelCreationStore: { isNextButtonClicked },
  } = useStore();

  const checkboxBlocks = useMemo(
    () =>
      currentStep?.elements
        ?.find((x) => x.name === 'agreement')
        ?.elements?.filter(
          (x) => x.type === 'row-block' || x.type === 'column-block'
        ) ?? [],
    [currentStep?.elements]
  );

  const checkboxElements = useMemo(
    () =>
      getNestedElements(checkboxBlocks.filter(isDefined)).filter(
        isCheckboxElement
      ),
    [checkboxBlocks]
  );

  const checkboxFormFieldNames = useMemo(
    () => checkboxElements.map((x) => x.behavior?.form_field).filter(isDefined),
    [checkboxElements]
  );

  const selectAllCheckboxName = useMemo(
    () =>
      checkboxElements.find((x) => x.type === 'input-checkbox-select-all')
        ?.behavior?.form_field,
    [checkboxElements]
  );

  const handleChecked = useCallback(
    (
      field: FieldInputProps<boolean>,
      formikContext: FormikContextType<CheckboxValues>
    ) => {
      const newValue = !field.checked;
      const { values } = formikContext;

      const formFieldNames = Object.keys(values);

      let newValues: CheckboxValues;

      if (field.name === selectAllCheckboxName) {
        newValues = formFieldNames.reduce(
          (acc, cur) => ({ ...acc, [cur]: newValue }),
          {}
        );
      } else {
        const checkAll =
          selectAllCheckboxName && !newValue
            ? {
                [selectAllCheckboxName]: false,
              }
            : {};
        newValues = {
          ...values,
          [field.name]: newValue,
          ...checkAll,
        };
      }
      formikContext.setValues(newValues, true);
      setSummaryConfirmFormValues(newValues);
    },
    [setSummaryConfirmFormValues, selectAllCheckboxName]
  );

  const checkboxElementComponents = useMemo(
    () =>
      checkboxBlocks
        .map((x) => prepareElementBlock(x, { handleChecked }))
        .filter(isDefined),
    [checkboxBlocks, handleChecked]
  );

  const initialValues: CheckboxValues = useMemo(
    () =>
      checkboxFormFieldNames.reduce(
        (acc, cur) => ({ ...acc, [cur]: !!summaryConfirmFormValues?.[cur] }),
        {}
      ),
    [checkboxFormFieldNames, summaryConfirmFormValues]
  );

  const validationSchema = checkboxElements.reduce((acc, cur) => {
    if (!cur.behavior?.required || !cur.behavior.form_field) return acc;

    return { ...acc, [cur.behavior.form_field]: Yup.boolean().oneOf([true]) };
  }, {});

  return (
    <SummaryCheckboxesSection
      $paddingBottom='3.2rem'
      $paddingLeft='2.4rem'
      $paddingRight='2.4rem'
      $paddingTop='2.4rem'
      data-testid={testIds.parceCreation.summary.checkboxes}>
      <FlexContainer $justify='start'>
        <Formik
          validationSchema={Yup.object(validationSchema)}
          initialValues={initialValues}
          onSubmit={() => {}}
          validateOnMount>
          {() => (
            <form>
              {checkboxElementComponents}

              <CheckboxesChangeHandler
                setSummaryFormIsValid={setSummaryFormIsValid}
                shouldSubmit={isNextButtonClicked}
              />
            </form>
          )}
        </Formik>
      </FlexContainer>
    </SummaryCheckboxesSection>
  );
};

export default observer(SummaryCheckboxSection);
